/**
 * Recent Comms (v2.5.6)
 *
 * Dashboard-level surface for the persistent comms log — closes the gap
 * where landlords had no visibility into outbound messages without
 * drilling into a specific property. Read-only, links out to the
 * property for full history.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { communicationService } from '../../../services/api';

interface Message {
    id: string;
    tenantId: string;
    propertyId?: string | null;
    channel: 'email' | 'sms';
    subject?: string;
    bodySnippet?: string | null;
    sentAt: string;
    deliveryStatus: 'sent' | 'failed' | 'bounced' | 'scheduled';
    source?: string;
}

const statusStyle = (s: Message['deliveryStatus']) => {
    switch (s) {
        case 'sent':      return 'bg-emerald-100 text-emerald-800';
        case 'scheduled': return 'bg-blue-100 text-blue-800';
        case 'failed':    return 'bg-red-100 text-red-800';
        case 'bounced':   return 'bg-amber-100 text-amber-800';
        default:          return 'bg-gray-100 text-gray-700';
    }
};

const RecentComms: React.FC = () => {
    const navigate = useNavigate();
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        communicationService.listRecentMessages(8)
            .then((res: any) => {
                if (!mounted) return;
                const list = res.data?.data || [];
                setMessages(Array.isArray(list) ? list : []);
            })
            .catch((err: any) => {
                if (!mounted) return;
                setError(err?.response?.data?.message || err?.message || 'Failed to load recent messages');
            })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-6 h-full overflow-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <i className="fas fa-envelope-open-text text-indigo-500 mr-2"></i>
                Recent Comms
            </h3>

            {loading ? (
                <div className="animate-pulse space-y-3">
                    <div className="h-10 bg-gray-100 rounded"></div>
                    <div className="h-10 bg-gray-100 rounded"></div>
                </div>
            ) : error ? (
                <p className="text-sm text-red-500">{error}</p>
            ) : messages.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                    <i className="fas fa-inbox text-2xl mb-2 block"></i>
                    <p className="text-sm">No messages sent yet.</p>
                </div>
            ) : (
                <ul className="divide-y divide-gray-100">
                    {messages.map((m) => (
                        <li
                            key={m.id}
                            className="py-2 cursor-pointer group"
                            onClick={() => m.propertyId && navigate(`/properties/${m.propertyId}`)}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900 truncate group-hover:text-indigo-700">
                                    {m.subject || '(no subject)'}
                                </p>
                                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex-shrink-0 ${statusStyle(m.deliveryStatus)}`}>
                                    {m.deliveryStatus}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate">{m.bodySnippet || 'No snippet stored'}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                                {m.channel.toUpperCase()} · {new Date(m.sentAt).toLocaleString('en-GB')}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default RecentComms;
